import { RadixTabs } from '@portal/components'
import React from 'react'

import { DQLResult } from '../../types'
import { findCollectionName } from '../../utils'
import DataImportMenu from '../dataImporter/DataImportMenu'
import { useDQLEditorContext } from '../dqlEditor/context'
import { SideBarOpener } from '../Sidebar'
import PollingIntervalMenu from './PollingIntervalMenu'
import QueryDataExport from './QueryDataExport'
import { DQLMutationResult } from './QueryResultTable'
import QueryResultWarningsPopover from './QueryResultWarningsPopover'

type QueryResultsTableHeaderProps = {
  title: string
  showResults: boolean
  action: 'export' | 'import'
  sideBarOpen: boolean
  data: DQLResult[] | DQLMutationResult[]
  selectedView: 'json' | 'table'
  onSelectedViewChange: (view: 'json' | 'table') => void
}

export default function QueryResultsTableHeader({
  title,
  showResults,
  action,
  data,
  selectedView,
  onSelectedViewChange,
}: QueryResultsTableHeaderProps) {
  const {
    activeView,
    query,
    sideBarOpen,
    setSideBarOpen,
    refetchInterval,
    setRefetchInterval,
  } = useDQLEditorContext()

  const handleViewChange = (view: string) => {
    if (view === 'json' || view === 'table') {
      onSelectedViewChange(view)
    }
  }

  return (
    <header
      data-testid="queryResultTableWrapperHeader"
      className="z-10 flex h-12 w-full shrink-0 items-center gap-x-4 border-b border-solid border-border-normal px-4"
    >
      {activeView === 'collections' ? (
        <SideBarOpener
          side="left"
          open={sideBarOpen}
          openHandler={() => setSideBarOpen(true)}
        >
          <span
            className="font-medium"
            data-testid="queryResultTableWrapperTitle"
          >
            {title}
          </span>
        </SideBarOpener>
      ) : (
        <RadixTabs
          defaultValue="json"
          value={selectedView}
          onValueChange={handleViewChange}
          className="w-full"
        >
          <RadixTabs.List>
            <RadixTabs.Trigger value="json" className="w-full">
              JSON
            </RadixTabs.Trigger>
            <RadixTabs.Trigger value="table" className="w-full">
              Table
            </RadixTabs.Trigger>
          </RadixTabs.List>
        </RadixTabs>
      )}

      <QueryResultWarningsPopover />
      <div className="flex-1" />

      {showResults && (
        <div className="flex-shrink-0">
          {data.length} result{data.length > 1 || !data.length ? 's' : ''}
        </div>
      )}

      <div className="flex items-center gap-2">
        {action === 'export' && (
          <QueryDataExport
            selectedCollection={
              query ? (findCollectionName(query) ?? null) : null
            }
            data={data}
          />
        )}
        {action === 'import' && (
          <>
            <PollingIntervalMenu
              pollingInterval={refetchInterval}
              setPollingInterval={setRefetchInterval}
            />

            <DataImportMenu />
          </>
        )}
      </div>
    </header>
  )
}
